import React, { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const CheckoutPage = () => {
  const { shopCode } = useParams();
  const history = useHistory();
  const location = useLocation();
  
  const { cart, shop } = location.state || {};
  const initialPricing = location.state?.pricing || null;
  const initialCoupon = location.state?.appliedCoupon || null;
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryPincode: '',
    deliveryLatitude: '',
    deliveryLongitude: '',
    orderType: 'DELIVERY',
    vehicleColor: '',
    vehicleNumber: '',
    paymentMethod: 'COD'
  });
  const [couponCode, setCouponCode] = useState(initialCoupon?.couponCode || initialPricing?.appliedCouponCode || '');
  const [appliedCoupon, setAppliedCoupon] = useState(initialCoupon);
  
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [deliverySettings, setDeliverySettings] = useState(shop?.deliverySettings || null);

  useEffect(() => {
    setDeliverySettings(shop?.deliverySettings || null);
  }, [shop]);

  useEffect(() => {
    const restaurantId = shop?.restaurantId || shop?.restaurant?.restaurantId || shop?.restaurant?.id || null;
    if (deliverySettings || !restaurantId) {
      return;
    }

    let isMounted = true;
    const loadDeliverySettings = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/vendor/delivery-settings/restaurant/${restaurantId}`);
        const payload = response?.data?.data || response?.data;
        if (!isMounted || !payload) return;
        setDeliverySettings(payload);
      } catch (error) {
        console.warn('Failed to load delivery settings for checkout:', error?.message || error);
      }
    };

    loadDeliverySettings();

    return () => {
      isMounted = false;
    };
  }, [deliverySettings, shop]);

  const requestCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported in this browser');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData((prev) => ({
          ...prev,
          deliveryLatitude: latitude.toFixed(6),
          deliveryLongitude: longitude.toFixed(6)
        }));
        toast.success('Current location captured');
        setGeoLoading(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Unable to get your current location. Please allow GPS permission.');
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  if (!cart || !shop) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">No items in cart</p>
          <button
            onClick={() => history.push(`/${shopCode}`)}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  const getCartSubtotal = () => {
    const subtotal = cart.reduce((total, item) => {
      const price = item.discountPrice || item.price;
      return total + (price * item.quantity);
    }, 0);
    return subtotal;
  };

  const isMrpCartItem = (item) => Boolean(
    item?.categoryIsMrp ?? item?.category_is_mrp ?? item?.isMrp ?? item?.is_mrp
  );

  const getTaxableCartSubtotal = () => {
    return cart.reduce((total, item) => {
      if (isMrpCartItem(item)) return total;
      const price = item.discountPrice || item.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const resolveDeliveryCharge = (subtotal) => {
    const settings = deliverySettings || shop?.deliverySettings || {};
    const safeSubtotal = Number(subtotal || 0);

    if (!settings?.isDeliveryEnabled) {
      return 0;
    }

    if (settings?.isSlabPricingEnabled) {
      const slabs = Array.isArray(settings?.slabs) ? settings.slabs : [];
      for (const slab of slabs) {
        const min = Number(slab?.minOrderAmount || 0);
        const max = slab?.maxOrderAmount === null || slab?.maxOrderAmount === undefined
          ? null
          : Number(slab?.maxOrderAmount);
        const inRange = safeSubtotal >= min && (max === null || safeSubtotal <= max);
        if (inRange) {
          return Number(slab?.deliveryCharge || 0);
        }
      }
    }

    return Number(settings?.fallbackDeliveryCharge || settings?.deliveryCharge || 0);
  };

  const getPricing = () => {
    const subtotal = getCartSubtotal();
    const deliveryCharge = formData.orderType === 'DELIVERY' ? resolveDeliveryCharge(subtotal) : 0;
    const rawDiscount = Number(appliedCoupon?.discountAmount || initialPricing?.discountAmount || 0);
    const discountAmount = Number.isFinite(rawDiscount) ? Math.max(rawDiscount, 0) : 0;
    // Tax calculation: exclude MRP items and do NOT tax delivery charge
    const gstSettings = shop?.gstSettings || shop?.gst || {};
    const taxPercentage = Number(gstSettings?.taxPercentage || 0);
    const taxEnabled = Boolean(gstSettings?.isTaxEnabled) && taxPercentage > 0;
    const taxableSubtotal = getTaxableCartSubtotal();
    const taxAmount = taxEnabled ? (taxableSubtotal * taxPercentage) / 100 : 0;

    const totalBeforeDiscount = subtotal + deliveryCharge + taxAmount;
    const total = Math.max(totalBeforeDiscount - discountAmount, 0);

    return {
      subtotal,
      deliveryCharge,
      discountAmount,
      totalBeforeDiscount,
      total,
      appliedCouponCode: appliedCoupon?.couponCode || initialPricing?.appliedCouponCode || null,
      couponId: appliedCoupon?.couponId || initialCoupon?.couponId || null,
      taxAmount,
      taxPercentage,
      taxEnabled,
      minimumOrderAmount: Number(shop?.deliverySettings?.minimumOrderAmountForDelivery || 0),
      belowMinimum: Number(shop?.deliverySettings?.minimumOrderAmountForDelivery || 0) > 0 && subtotal < Number(shop?.deliverySettings?.minimumOrderAmountForDelivery || 0)
    };
  };

  const getPickupAddressFromShop = () => {
    const line1 = shop?.addressLine1 || '';
    const line2 = shop?.addressLine2 || '';
    return `${line1}${line1 && line2 ? ', ' : ''}${line2}`.trim();
  };

  const getShopLatitude = () => {
    return shop?.latitude ?? shop?.shopLatitude ?? null;
  };

  const getShopLongitude = () => {
    return shop?.longitude ?? shop?.shopLongitude ?? null;
  };

  const handleApplyCoupon = async () => {
    const code = String(couponCode || '').trim().toUpperCase();
    if (!code) {
      toast.error('Please enter a coupon code');
      return;
    }

    const pricing = getPricing();
    if (pricing.totalBeforeDiscount <= 0) {
      toast.error('Cart total must be greater than 0');
      return;
    }

    setCouponLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/coupons/apply`, {
        shopId: shop?.shopId,
        couponCode: code,
        amount: Number(pricing.totalBeforeDiscount.toFixed(2))
      });

      if (!response?.data?.success || !response?.data?.data) {
        throw new Error(response?.data?.message || 'Failed to apply coupon');
      }

      setAppliedCoupon(response.data.data);
      setCouponCode(response.data.data.couponCode || code);
      toast.success('Coupon applied successfully');
    } catch (error) {
      setAppliedCoupon(null);
      toast.error(error.response?.data?.message || error.message || 'Invalid coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validation per order type
    if (formData.orderType === 'DELIVERY') {
      if (!formData.deliveryLatitude || !formData.deliveryLongitude) {
        toast.error('Please capture your current location before placing order');
        return;
      }
      if (!formData.deliveryAddress || !formData.deliveryCity || !formData.deliveryPincode) {
        toast.error('Please fill delivery address, city and pincode');
        return;
      }
    }

    if (formData.orderType === 'DRIVETHROUGH') {
      if (!formData.vehicleNumber) {
        toast.error('Please enter vehicle number for drive-through orders');
        return;
      }
    }
    
    try {
      setLoading(true);

      const buildSelectedAddonIds = (item) => {
        const selectedAddons = Array.isArray(item?.selectedAddons) ? item.selectedAddons : [];
        return selectedAddons
          .filter((addon) => !!(addon?.legacyAddonId || addon?.addonId) && !addon?.optionId)
          .map((addon) => Number(addon.legacyAddonId || addon.addonId));
      };

      const buildModifierSelections = (item) => {
        const selectedAddons = Array.isArray(item?.selectedAddons) ? item.selectedAddons : [];
        return selectedAddons.reduce((acc, addon) => {
          const groupId = addon?.groupId;
          const optionId = addon?.optionId;
          if (!groupId || !optionId) {
            return acc;
          }

          const key = String(groupId);
          if (!acc[key]) {
            acc[key] = [];
          }
          if (!acc[key].includes(Number(optionId))) {
            acc[key].push(Number(optionId));
          }
          return acc;
        }, {});
      };
      
      const mappedItems = cart.map(item => ({
        productId: item.productId,
        productName: item.productName,
        quantity: Number(item.quantity || 0),
        unitPrice: Number(item.discountPrice ?? item.price ?? 0),
        specialInstructions: item.specialInstructions || '',
        selectedAddonIds: buildSelectedAddonIds(item),
        selectedModifiers: buildModifierSelections(item),
      }));

      const orderItemsPayload = mappedItems.map((it) => ({
        product: { productId: it.productId, productName: it.productName },
        quantity: it.quantity,
        unitPrice: it.unitPrice,
        specialInstructions: it.specialInstructions,
        selectedAddonIds: it.selectedAddonIds,
        selectedModifiers: it.selectedModifiers,
        addons: it.selectedAddonIds.map((addonId) => ({ addonId })),
        modifierOptionIds: Object.values(it.selectedModifiers || {}).flat(),
      }));

      const orderData = {
        ...formData,
        orderType: formData.orderType || 'DELIVERY',
        paymentStatus: 'COMPLETED',
        restaurantId: shop?.restaurantId || null,
        deliveryLatitude: Number(formData.deliveryLatitude),
        deliveryLongitude: Number(formData.deliveryLongitude),
        pickupAddress: getPickupAddressFromShop(),
        pickupCity: shop?.city || '',
        pickupPincode: shop?.pincode || shop?.postalCode || '',
        vehicleColor: formData.vehicleColor || null,
        vehicleNumber: formData.vehicleNumber || null,
        pickupLatitude: getShopLatitude(),
        pickupLongitude: getShopLongitude(),
        couponId: appliedCoupon?.couponId || null,
        couponCode: appliedCoupon?.couponCode || null,
        discountAmount: Number(appliedCoupon?.discountAmount || 0),
        deliveryCharge: Number(pricing.deliveryCharge || 0),
        taxAmount: Number(pricing.taxAmount || 0),
        taxPercentage: Number(pricing.taxPercentage || 0),
        // Keep legacy `items` shape for existing backend handlers
        items: mappedItems.map(it => ({
          productId: it.productId,
          quantity: it.quantity,
          specialInstructions: it.specialInstructions,
          selectedAddonIds: it.selectedAddonIds,
          selectedModifiers: it.selectedModifiers,
          addons: it.selectedAddonIds.map((addonId) => ({ addonId })),
          modifierOptionIds: Object.values(it.selectedModifiers || {}).flat(),
        })),
        // Add richer snapshots so downstream sync/pos systems can consume full item info
        latestCartSnapshot: mappedItems.map(it => ({
          name: it.productName,
          quantity: it.quantity,
          price: it.unitPrice,
          productId: it.productId,
          selectedAddonIds: it.selectedAddonIds,
          selectedModifiers: it.selectedModifiers,
        })),
        orderItems: orderItemsPayload
      };
      
      const response = await axios.post(
        `${API_BASE_URL}/api/vendor/orders/shop/${shop.shopId}/create`,
        orderData
      );
      
      if (response.data.success) {
        toast.success('Order placed successfully!');
        history.push(`/${shopCode}/order-success/${response.data.data.orderId}`);
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const pricing = getPricing();
  const { subtotal, deliveryCharge, discountAmount, total } = pricing;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Checkout</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Checkout Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Order Details</h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Type *</label>
                    <div className="flex gap-3">
                      <label className={`p-2 border rounded-lg cursor-pointer ${formData.orderType==='DELIVERY' ? 'bg-gray-100' : ''}`}>
                        <input type="radio" name="orderType" value="DELIVERY" checked={formData.orderType === 'DELIVERY'} onChange={(e) => setFormData({...formData, orderType: e.target.value})} /> Delivery
                      </label>
                      <label className={`p-2 border rounded-lg cursor-pointer ${formData.orderType==='TAKEAWAY' ? 'bg-gray-100' : ''}`}>
                        <input type="radio" name="orderType" value="TAKEAWAY" checked={formData.orderType === 'TAKEAWAY'} onChange={(e) => setFormData({...formData, orderType: e.target.value})} /> Takeaway
                      </label>
                      <label className={`p-2 border rounded-lg cursor-pointer ${formData.orderType==='DRIVETHROUGH' ? 'bg-gray-100' : ''}`}>
                        <input type="radio" name="orderType" value="DRIVETHROUGH" checked={formData.orderType === 'DRIVETHROUGH'} onChange={(e) => setFormData({...formData, orderType: e.target.value})} /> Drive-through
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.customerName}
                      onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={formData.customerPhone}
                      onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
                    <input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  {formData.orderType === 'DELIVERY' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address *</label>
                      <textarea
                        required
                        value={formData.deliveryAddress}
                        onChange={(e) => setFormData({...formData, deliveryAddress: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Enter complete delivery address"
                      />
                    </div>
                  )}

                  {formData.orderType === 'DELIVERY' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                        <input
                          type="text"
                          required
                          value={formData.deliveryCity}
                          onChange={(e) => setFormData({...formData, deliveryCity: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter city"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                        <input
                          type="text"
                          required
                          value={formData.deliveryPincode}
                          onChange={(e) => setFormData({...formData, deliveryPincode: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter pincode"
                        />
                      </div>
                    </div>
                  )}

                  {formData.orderType === 'DELIVERY' && (
                    <div className="border rounded-lg p-4 bg-blue-50">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <label className="block text-sm font-medium text-gray-700">Delivery GPS Location *</label>
                        <button
                          type="button"
                          onClick={requestCurrentLocation}
                          disabled={geoLoading}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {geoLoading ? 'Capturing...' : 'Use Current Location'}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          readOnly
                          value={formData.deliveryLatitude}
                          className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                          placeholder="Latitude"
                        />
                        <input
                          type="text"
                          readOnly
                          value={formData.deliveryLongitude}
                          className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                          placeholder="Longitude"
                        />
                      </div>
                    </div>
                  )}

                  {formData.orderType === 'DRIVETHROUGH' && (
                    <div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Colour *</label>
                        <input
                          type="text"
                          required
                          value={formData.vehicleColor}
                          onChange={(e) => setFormData({...formData, vehicleColor: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., White, Black"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number *</label>
                        <input
                          type="text"
                          required
                          value={formData.vehicleNumber}
                          onChange={(e) => setFormData({...formData, vehicleNumber: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Vehicle registration number"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="COD"
                          checked={formData.paymentMethod === 'COD'}
                          onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                          className="w-4 h-4"
                        />
                        <span className="font-medium">Cash on Delivery</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="ONLINE"
                          checked={formData.paymentMethod === 'ONLINE'}
                          onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                          className="w-4 h-4"
                        />
                        <span className="font-medium">Online Payment</span>
                      </label>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition font-bold text-lg disabled:opacity-50"
                  >
                    {loading ? 'Placing Order...' : `Place Order (₹${total.toFixed(2)})`}
                  </button>
                </form>
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>
                
                <div className="space-y-3 mb-4">
                  {cart.map(item => (
                    <div key={item.productId} className="flex justify-between text-sm">
                      <span className="text-gray-700">
                        {item.productName} x {item.quantity}
                      </span>
                      <span className="font-medium text-gray-800">
                        ₹{((item.discountPrice || item.price) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-800">₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery Charges</span>
                    <span className="font-medium text-gray-800">₹{deliveryCharge.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">Discount {pricing.appliedCouponCode ? `(${pricing.appliedCouponCode})` : ''}</span>
                      <span className="font-medium text-green-600">-₹{discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span className="text-gray-800">Total</span>
                    <span className="text-green-600">₹{total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 border rounded-lg p-4 bg-slate-50">
                  <h3 className="text-sm font-bold text-gray-700 mb-3">Coupon</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(String(e.target.value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter coupon code"
                      disabled={loading || couponLoading}
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={loading || couponLoading}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
                    >
                      {couponLoading ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                  {appliedCoupon && (
                    <div className="mt-3 flex items-center justify-between rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                      <div className="text-sm text-green-800">
                        Applied {appliedCoupon.couponCode} • Saved ₹{Number(appliedCoupon.discountAmount || 0).toFixed(2)}
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveCoupon}
                        className="px-3 py-1.5 rounded-lg bg-green-100 text-green-800 text-sm font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="mt-6 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    <strong>Delivering to:</strong> {shop.city}, {shop.state}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    <strong>Shop:</strong> {shop.shopName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
